import { CanvasScene } from "./CanvasScene";
import { GameVars } from "../../GameVars";
import { GameConstants } from "../../GameConstants";
import { GameManager } from "../../GameManager";
import { GuiScene } from "../GUI/GuiScene";
import { VanisheCircle, GeneralUtils } from "../../utils/Utils";
import { MyPoint } from "../../../typescript/MyPoint";
import { AudioManager } from "../../AudioManager";
import { Cell } from "./Cell";
import { CellsPool } from "../../utils/CellsPool";

export class CanvasManager {

    public static currentScene: CanvasScene;

    // Draw Info
    public static tilesAmmount: number;
    public static tilesDone: number;
    public static widthInPixels: number;
    public static widthInCells: number;
    public static heightInPixels: number;
    public static heightInCells: number;
    public static cellWidthInPixels: number;
    public static cellHeightInPixels: number;

    // Will tell if the user is painting a pixel 
    private static dataSaved: boolean;

    public static init(scene: CanvasScene) {

        AudioManager.SetVolume(0.4);

        GameVars.drawColors = [];
        GameVars.colorCount = [];
        GameVars.colorCountMax = [];
        GameVars.pixelTilesCode = [];
        GameVars.pixelTilesCodeGrayscale = [];
        GameVars.pixelTiles = [];

        CanvasManager.currentScene = scene;
        CanvasManager.dataSaved = false;

        let catString = GameManager.getFileFormatCategory(GameVars.currentDrawCategory);

        let drawFileName = catString + "_draw_" + GeneralUtils.getAsPaddedNumber(GameVars.currentDrawId + 1, 2) + ".png";
        let drawFileNameGS = catString + "_draw_gs_" + GeneralUtils.getAsPaddedNumber(GameVars.currentDrawId + 1, 2) + ".png";

        // Will fill pixelTilesCode Matrix with RGB colors
        CanvasManager.parsePNGToMat("texture_atlas_0", drawFileName);

        // Will fill pixelTilesCodeGrayscale Matrix with GrayScale colors
        CanvasManager.parsePNG_GS_ToMat("texture_atlas_0", drawFileNameGS);

        // Initialize data 
        this.cellWidthInPixels = 32;
        this.cellHeightInPixels = 32;
        GameVars.drawColors = [];
        this.tilesAmmount = 0;
        this.tilesDone = 0;

        this.widthInPixels = (this.widthInCells * this.cellWidthInPixels);
        this.heightInPixels = (this.heightInCells * this.cellHeightInPixels);
        GameVars.paletteHeight = (GameConstants.GAME_WIDTH / 6) * 2;

        if (GameConstants.DEVELOPMENT) {
            this.currentScene.input.keyboard.on("keydown-O", function () {
                this.paintAllCells(true);
            }, this);
        }
    }

    public static sendCellsToPool(): void {

        for (let y = 0; y < this.heightInCells; y++) {
            for (let x = 0; x < this.widthInCells; x++) {
                if (GameVars.pixelTiles[y][x] !== null) {
                    CellsPool.push(GameVars.pixelTiles[y][x]);
                }
            }
        }
    }

    public static getCellFromScreenPos(): { x: number, y: number } {

        // Create variable for the cell that is going to be get & 2 numbers for finding the cell in the array 
        let newCell: Cell;
        let arrayX: number = -1;
        let arrayY: number = -1;

        // Create an invisible "anchor" that will be used as reference 
        // "displayIncrement" variables hold the distance between the end of the draw and the border of the screen
        let distanceScaledX = (64 * GameVars.scaleXMultInverse) / 2;
        let distanceScaledY = (64 * GameVars.scaleYMultInverse) / 2 + (GameVars.paletteHeight / 2);

        let originX = CanvasManager.currentScene.board.x - ((CanvasManager.widthInPixels / 2) * GameVars.scaleXMult);
        let originY = CanvasManager.currentScene.board.y - ((CanvasManager.heightInPixels / 2) * GameVars.scaleYMult);

        // Get the position of the mouse relative to the anchor point
        let pixelPosInDrawX = this.currentScene.input.activePointer.worldX - originX;
        let pixelPosInDrawY = this.currentScene.input.activePointer.worldY - originY;

        pixelPosInDrawX *= GameVars.scaleXMultInverse;
        pixelPosInDrawY *= GameVars.scaleYMultInverse;

        // Transfrom the position to cell space
        arrayX = Phaser.Math.FloorTo((pixelPosInDrawX * CanvasManager.currentScene.camManager.zoom) / (32 * CanvasManager.currentScene.camManager.zoom));
        arrayY = Phaser.Math.FloorTo((pixelPosInDrawY * CanvasManager.currentScene.camManager.zoom) / (32 * CanvasManager.currentScene.camManager.zoom));

        // Filter out of draw values 
        if (arrayX < 0 || arrayX > CanvasManager.widthInCells) {
            return null;
        }

        if (arrayY < 0 || arrayY >= CanvasManager.heightInCells) {
            return null;
        }

        if (GameVars.pixelTiles[arrayY][arrayX] === null) {
            return null;
        }

        let point: { x: number, y: number } = {
            x: arrayX,
            y: arrayY
        };

        return point;
    }

    public static applySavedData(): void { // Will feel saved pixels

        if (!GameManager.isDrawSaved(GameVars.currentDrawCategoryID, GameVars.currentDrawId)) {
            return;
        }

        let pixelInfo = GameManager.getPixelData(GameVars.currentDrawCategoryID, GameVars.currentDrawId);

        if (pixelInfo[2] <= 0.5) {

            for (let i = 3; i < pixelInfo.length; i++) {
                this.paintCell(pixelInfo[i]);
            }

        } else {

            for (let y = 0; y < this.heightInCells; y++) {
                for (let x = 0; x < this.widthInCells; x++) {

                    let cell = GameVars.pixelTiles[y][x];

                    if (cell === null) {
                        continue;
                    }

                    let id = cell.tileID;
                    let found = false;

                    for (let i = 3; i < pixelInfo.length; i++) {

                        if (pixelInfo[i] === id) {
                            found = true;
                            break;
                        }
                    }

                    if (!found) {
                        this.paintCell(id);
                    }

                }
            }
        }
    }

    public static update(delta: number): void {

        // Update Cells 
        GameVars.pixelTiles.forEach(currentCell => {
            currentCell.forEach(currentCell => {

                if (currentCell !== null) {
                    currentCell.update(delta);
                }

            });
        });

        // Check if any cell needs to be remarked         
        if (this.currentScene.registry.get("ColorPressed") != null) {
            CanvasManager.remarkCellsByID();
            this.currentScene.registry.reset();
        }

        // Check if we hae to save 
        if (CanvasManager.tilesDone % 50 === 0 && CanvasManager.tilesDone !== 0 && CanvasManager.tilesDone !== CanvasManager.tilesAmmount) {

            if (!this.dataSaved) {
                GameManager.saveDrawData();
                GameManager.writeGameData();
                this.dataSaved = true;
            }
        } else {
            this.dataSaved = false;
        }
    }

    public static isMouseInDraw(): boolean {

        for (let y = 0; y < this.heightInCells; y++) {
            for (let x = 0; x < this.widthInCells; x++) {

                if (GameVars.pixelTiles[y][x] === null) {
                    continue;
                }

                if (GameVars.pixelTiles[y][x].isMouseInside()) {
                    return true;
                }
            }
        }

        return false;
    }

    public static paintAllCells(save: boolean): void {

        for (let i = 0; i < this.widthInCells * this.heightInCells; i++) {
            this.paintCell(i, false);
        }

        if (save) {

            GameManager.deletePixelData(GameVars.currentDrawCategoryID, GameVars.currentDrawId);
            GameManager.setDrawState(GameVars.currentDrawCategoryID, GameVars.currentDrawId, drawState.DRAW_FINISHED);

            GameVars.recentDrawFinishedCategoryID = GameVars.currentDrawCategoryID;
            GameVars.recentDrawFinishedID = GameVars.currentDrawId;

            GameManager.writeGameData();
        }

    }

    public static paintCell(cell_pos: number, playSound: boolean = false): void {

        let arrayPos = CanvasManager.posTo2DArray(cell_pos);
        let cell = GameVars.pixelTiles[arrayPos.x][arrayPos.y];

        if (cell !== null) {
            GameVars.currentPixelColorID = cell.colorID;
            cell.swapColor(true, playSound);
        } else {
            return;
        }
    }

    public static remarkCellsByID(): void {  // Will set tile color a little bit darker than it's to remark itsef. 

        GameVars.pixelTiles.forEach(pixelRow => {
            pixelRow.forEach(currentCell => {

                if (currentCell !== null) {
                    if (currentCell.colorID === GameVars.currentPixelColorID) {
                        currentCell.setRemarked(true);
                    } else {
                        currentCell.setRemarked(false);
                    }
                }
            });
        });
    }

    public static resetProgress(): void {

        this.tilesDone = 0;
        this.resetColorCount();
    }

    public static substractCell(cellID: number): void {

        if (GameVars.colorCount[cellID - 1] > 0) {
            GameVars.colorCount[cellID - 1]--;

            if (GameVars.colorCount[cellID - 1] === 0) {
                GuiScene.currentInstance.checkmarkPaletteElementWithID(cellID);
            }
        }
    }

    public static getDrawFillPercentage(): number { // 0 = empty // 1 = filled   

        if (this.tilesDone > this.tilesAmmount) {
            GameManager.log("Tile count discrepancy");
        }
        return Math.floor(this.tilesDone / this.tilesAmmount * 1e3) / 1e3;
    }

    public static addColor(newColor: Phaser.Display.Color) {  // Add new color to palette 

        if (!this.existColor(newColor)) {
            GameVars.drawColors.push(newColor);
            GameVars.colorCount.push(1);
            GameVars.colorCountMax.push(1);
        }
    }

    public static getColorsAmmount(): number { // Get the number of colors the draw has 
        return GameVars.drawColors.length;
    }

    public static getColorID(newColor: Phaser.Display.Color): number { // Returns the ID from a given color, if not found return -1

        let counter = 0;

        for (let i = 0; i < GameVars.drawColors.length; i++) {
            if (GameVars.drawColors[i].color === newColor.color) {
                return counter + 1;
            }
            counter++;
        }
        return -1;
    }

    public static existColor(newColor: Phaser.Display.Color): boolean { // Returns true if the color already exist in palette

        for (let i = 0; i < GameVars.drawColors.length; i++) {
            if (GameVars.drawColors[i].color === newColor.color) {
                return true;
            }
        }

        return false;
    }

    public static paintCurrentColor(): void {

        for (let y = 0; y < this.widthInCells; y++) {
            for (let x = 0; x < this.widthInCells; x++) {

                if (GameVars.pixelTiles[y][x].colorID === GameVars.currentPixelColorID) {
                    GameVars.pixelTiles[y][x].swapColor(true);
                }
            }
        }
    }

    public static checkWin(): boolean { // Returns true if the user has finished the draw 

        // Draw will be finished when all pixelCount === 0 
        for (let i = 0; i < GameVars.colorCount.length; i++) {
            if (GameVars.colorCount[i] > 0) {
                return false;
            }
        }

        return true;
    }

    private static resetColorCount(): void {

        for (let i = 0; i < GameVars.colorCountMax.length; i++) {
            GameVars.colorCount[i] = GameVars.colorCountMax[i];
        }
    }

    private static posTo2DArray(pos: number): MyPoint {

        const x = Phaser.Math.FloorTo(pos / CanvasManager.widthInCells);
        const y = pos % CanvasManager.widthInCells;

        const ret_value: MyPoint = new MyPoint(x, y);
        return ret_value;
    }

    private static parsePNGToMat(atlasKey: string, frameKey: string): void {

        // Get size of needed image
        const atlas = CanvasManager.currentScene.textures.get(atlasKey);
        const frame = atlas.get(frameKey);

        CanvasManager.widthInCells = frame.cutWidth;
        CanvasManager.heightInCells = frame.cutHeight;

        // Allocate to matrix
        GameVars.pixelTilesCode = [];

        let canvasTexture: Phaser.Textures.CanvasTexture;

        if (GameVars.canvasTextures.hasOwnProperty(frameKey)) {
            canvasTexture = GameVars.canvasTextures[frameKey];
        } else {
            canvasTexture = CanvasScene.currentInstance.textures.createCanvas(frameKey, frame.cutWidth, frame.cutHeight);
            canvasTexture.drawFrame(atlasKey, frameKey, 0, 0);
            GameVars.canvasTextures[frameKey] = canvasTexture;
        }

        for (let y = 0; y < CanvasManager.heightInCells; y++) {
            GameVars.pixelTilesCode[y] = [];
            for (let x = 0; x < CanvasManager.widthInCells; x++) {

                const pixel = canvasTexture.getPixel(x, y);
                GameVars.pixelTilesCode[y].push(pixel);
            }
        }
    }

    private static parsePNG_GS_ToMat(atlasKey: string, frameKey: string): void {

        // Get size of needed image
        let atlas = CanvasManager.currentScene.textures.get(atlasKey);
        let frame = atlas.get(frameKey);

        CanvasManager.widthInCells = frame.cutWidth;
        CanvasManager.heightInCells = frame.cutHeight;

        let canvasTexture: Phaser.Textures.CanvasTexture;

        if (GameVars.canvasTextures.hasOwnProperty(frameKey + "_gs")) {
            canvasTexture = GameVars.canvasTextures[frameKey + "_gs"];
        } else {
            canvasTexture = CanvasScene.currentInstance.textures.createCanvas(frameKey + "_gs", frame.cutWidth, frame.cutHeight);
            canvasTexture.drawFrame(atlasKey, frameKey, 0, 0);
            GameVars.canvasTextures[frameKey + "_gs"] = canvasTexture;
        }

        // Allocate to matrix
        GameVars.pixelTilesCodeGrayscale = [];
        for (let y = 0; y < CanvasManager.heightInCells; y++) {
            GameVars.pixelTilesCodeGrayscale[y] = [];
            for (let x = 0; x < CanvasManager.widthInCells; x++) {

                const pixel = canvasTexture.getPixel(x, y);
                GameVars.pixelTilesCodeGrayscale[y].push(pixel);
            }
        }
    }
}
